import type { ReactNode } from 'react';
import { Banner, Kbd } from '../components/ui';
import { track } from '../lib/amplitude';
import { useHotkeys } from '../lib/useHotkeys';
import { AudioStage } from './AudioStage';
import { ModePicker, SourceBar, TransportBar } from './RecorderControls';
import { RecorderStage } from './RecorderStage';
import type { UploadTransport } from './uploader';
import { useRecorderSession, type RecorderApi, type RecorderSession } from './useRecorder';

/**
 * The recorder, assembled. Both /new and /r/:token mount this same surface;
 * they differ only in the chrome around it and in what the preview screen says
 * once the take is done.
 *
 * Two modes, one machine. The mode picker is the first control on the page
 * because it decides what every control below it means, and because "record
 * audio only" is a different intention, not a smaller version of the same one.
 *
 * Who owns the session is the caller's choice. `<Recorder>` opens one and
 * releases it on unmount, which is all /r/:token wants. `<RecorderSurface>`
 * takes one the page already holds, which is what /new needs: a full screen
 * mode has to read the phase before it will let anybody leave.
 */

export interface RecorderSurfaceProps {
  session: RecorderSession;
  /** Rendered instead of the stage once a take is finished. */
  renderPreview: (recorder: RecorderApi) => ReactNode;
  /** Shown when the browser cannot capture a screen. */
  renderUnsupported?: () => ReactNode;
  /** Extra content under the source bar (destination pickers, name fields). */
  children?: ReactNode;
  onCancel?: () => void;
  /**
   * Hold the recorder's keyboard shortcuts while something modal sits over it.
   * Enter meaning "finish this take" and "answer the dialog" at the same moment
   * is exactly the kind of accident this recorder exists to prevent.
   */
  suspendHotkeys?: boolean;
}

export function RecorderSurface({
  session,
  renderPreview,
  renderUnsupported,
  children,
  onCancel,
  suspendHotkeys = false,
}: RecorderSurfaceProps) {
  const { recorder, mode, setMode } = session;
  const { phase, support } = recorder;

  useHotkeys(
    {
      space: () => {
        if (phase === 'ready') recorder.start();
        else if (phase === 'countdown') recorder.skipCountdown();
        else recorder.togglePause();
      },
      enter: () => {
        if (phase === 'recording' || phase === 'paused') void recorder.stop('hotkey');
      },
      'shift+r': () => recorder.restart(),
      escape: () => {
        if (phase === 'countdown') recorder.skipCountdown();
      },
    },
    !suspendHotkeys && phase !== 'preview' && phase !== 'unsupported',
  );

  if (phase === 'unsupported') {
    return (
      <div className="space-y-4">
        <Banner tone="caution" title="This browser cannot record">
          Recording needs Chrome, Edge, Firefox, or Safari 16+, and a page served over HTTPS.
          Screen capture additionally needs a desktop: phones cannot capture their own screen from
          a browser at all.
        </Banner>
        {renderUnsupported?.()}
      </div>
    );
  }

  if (phase === 'preview') return <>{renderPreview(recorder)}</>;

  const audio = mode === 'audio';
  // The picker locks once a take is under way: sources cannot be swapped from
  // under a running MediaRecorder, and switching would throw the take away.
  const modeLocked = phase !== 'idle' && phase !== 'ready';

  return (
    <div className="space-y-5">
      <ModePicker
        mode={mode}
        onChange={(next) => {
          track('Recording Source Selected', { source: next === 'audio' ? 'audio' : 'screen' });
          setMode(next);
        }}
        disabled={modeLocked}
        screenAvailable={support.screenOk}
        audioAvailable={support.audioOk}
      />

      {!support.screenOk ? (
        <Banner tone="caution" title="This device cannot capture a screen">
          Phones cannot record their own screen from a browser: that is an OS limitation, not a
          Slate one. An audio note works fine here.
        </Banner>
      ) : null}

      {audio ? <AudioStage recorder={recorder} /> : <RecorderStage recorder={recorder} />}

      {recorder.error ? (
        <Banner tone="danger" title={audio ? 'Microphone problem' : 'Capture problem'}>
          {recorder.error}
        </Banner>
      ) : null}

      {recorder.upload.error && (phase === 'recording' || phase === 'paused') ? (
        <Banner tone="caution" title="The upload is struggling">
          {recorder.upload.error} Keep recording: parts are retried, and anything still
          outstanding is flushed when you finish.
        </Banner>
      ) : null}

      <SourceBar recorder={recorder} />

      {children}

      <div className="rounded-md border border-hairline bg-slate-850 p-4">
        <TransportBar recorder={recorder} onCancel={onCancel} />
      </div>

      {phase === 'idle' || phase === 'ready' ? (
        <p className="flex flex-wrap items-center gap-1.5 text-caption text-chalk-faint">
          Once you are rolling: <Kbd>Space</Kbd> pauses and resumes,{' '}
          <Kbd>Shift</Kbd>
          <Kbd>R</Kbd> throws the take away and starts again without{' '}
          {audio ? 're-asking for your microphone' : 're-picking your screen'}, and{' '}
          <Kbd>Enter</Kbd> finishes.
          {recorder.codec ? (
            <span className="ml-1 text-chalk-faint/70">Recording as {recorder.codec.label}.</span>
          ) : null}
        </p>
      ) : null}

      {/* Whatever a page offers a browser that cannot capture a screen (on
          /new, uploading a file instead) still belongs here when only the
          audio half of the recorder works. */}
      {!support.screenOk && renderUnsupported ? (
        <div className="space-y-4 border-t border-hairline pt-5">{renderUnsupported()}</div>
      ) : null}
    </div>
  );
}

export interface RecorderProps extends Omit<RecorderSurfaceProps, 'session'> {
  transport: UploadTransport;
  makeTitle: () => string;
}

/**
 * The recorder over a session of its own. For pages with no need to see inside
 * the machine: unmounting this releases the microphone and the screen share.
 */
export function Recorder({
  transport,
  makeTitle,
  ...surface
}: RecorderProps) {
  const session = useRecorderSession({ transport, makeTitle });
  return <RecorderSurface session={session} {...surface} />;
}
